import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './index.css';

// Data — JSON-based translations
import translations from './data/data.json';

// Components
import UtilityBar from './components/UtilityBar';
import MainNav from './components/MainNav';
import Hero from './components/Hero';
import Stats from './components/Stats';
import About from './components/About';
import WhyGMIS from './components/WhyGMIS';
import Accreditations from './components/Accreditations';
import Learners from './components/Learners';
import Locations from './components/Locations';
import CTA from './components/CTA';
import Footer from './components/Footer';
import Contact from './components/Contact';
import AboutUsPage from './components/AboutUsPage';
import ComingSoonPage from './components/ComingSoonPage';
import VisionMissionPage from './components/VisionMissionPage';
import GandhianValuesPage from './components/GandhianValuesPage';
import NewsletterPage from './components/NewsletterPage';
import LatestNewsPage from './components/LatestNewsPage';
import FaqsPage from './components/FaqsPage';
import PreschoolPage from './components/PreschoolPage';
import PrimarySchoolPage from './components/PrimarySchoolPage';
import MiddleSchoolPage from './components/MiddleSchoolPage';
import SeniorSchoolPage from './components/SeniorSchoolPage';
import AcademicCalendarPage from './components/AcademicCalendarPage';
import SchoolEventsPage from './components/SchoolEventsPage';
import WellbeingSafeguardingPage from './components/WellbeingSafeguardingPage';
import StudentLeadershipPage from './components/StudentLeadershipPage';
import PlayProductionPage from './components/PlayProductionPage';
import AlumniCommunityPage from './components/AlumniCommunityPage';
import HouseSystemPage from './components/HouseSystemPage';
import ExtracurricularActivitiesPage from './components/ExtracurricularActivitiesPage';
import AdmissionsProcessPage from './components/AdmissionsProcessPage';
import FeesTuitionPage from './components/FeesTuitionPage';
import ScheduleVisitPage from './components/ScheduleVisitPage';
import CurriculumApproachPage from './components/CurriculumApproachPage';
import AccreditationLicensesPage from './components/AccreditationLicensesPage';
import LeadershipFacultyPage from './components/LeadershipFacultyPage';
import SchoolFacilitiesPage from './components/SchoolFacilitiesPage';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';

function AppContent() {
  const [lang, setLang] = useState('en');
  const [slideIdx, setSlide] = useState(0);
  const [whyIdx, setWhyIdx] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Derive current page for components that need it
  const path = location.pathname.replace('/', '');
  const page = path || 'home';

  // Sync state changes to URL
  const setPage = (newPage) => {
    navigate(newPage === 'home' ? '/' : `/${newPage}`);
  };

  const t = translations[lang];

  return (
    // Each section handles its own full-width background + inner max-w-[1440px] content
    <div style={{ fontFamily: "'Public Sans', system-ui, sans-serif", color: 'var(--ink)' }}>
      <UtilityBar t={t} lang={lang} setLang={setLang} setPage={setPage} />
      <MainNav t={t} lang={lang} setLang={setLang} page={page} setPage={setPage} />

      <Routes>
        <Route path="/contact" element={<Contact t={t} />} />
        <Route path="/inquiry" element={<Contact t={t} />} />
        <Route path="/inquiry-form" element={<Contact t={t} />} />
        <Route path="/enquiry" element={<Contact t={t} />} />
        <Route path="/enquiry-form" element={<Contact t={t} />} />
        <Route path="/about" element={<AboutUsPage setPage={setPage} t={t} />} />
        <Route path="/history" element={<AboutUsPage setPage={setPage} t={t} />} />
        <Route path="/gmis-history" element={<AboutUsPage setPage={setPage} t={t} />} />
        <Route path="/our-history" element={<AboutUsPage setPage={setPage} t={t} />} />
        <Route path="/vision-mission" element={<VisionMissionPage setPage={setPage} t={t} />} />
        <Route path="/gandhian-values" element={<GandhianValuesPage setPage={setPage} t={t} />} />
        <Route path="/newsletter" element={<NewsletterPage setPage={setPage} t={t} />} />
        <Route path="/latest-news" element={<LatestNewsPage setPage={setPage} t={t} />} />
        <Route path="/faqs" element={<FaqsPage setPage={setPage} t={t} />} />
        <Route path="/admissions-process" element={<AdmissionsProcessPage setPage={setPage} t={t} />} />
        <Route path="/admissions" element={<AdmissionsProcessPage setPage={setPage} t={t} />} />
        <Route path="/admission-process" element={<AdmissionsProcessPage setPage={setPage} t={t} />} />
        <Route path="/fees-tuition" element={<FeesTuitionPage setPage={setPage} t={t} />} />
        <Route path="/fees" element={<FeesTuitionPage setPage={setPage} t={t} />} />
        <Route path="/tuition" element={<FeesTuitionPage setPage={setPage} t={t} />} />
        <Route path="/school-fees" element={<FeesTuitionPage setPage={setPage} t={t} />} />
        <Route path="/schedule-visit" element={<ScheduleVisitPage setPage={setPage} t={t} />} />
        <Route path="/schedule-a-visit" element={<ScheduleVisitPage setPage={setPage} t={t} />} />
        <Route path="/visit" element={<ScheduleVisitPage setPage={setPage} t={t} />} />
        <Route path="/curriculum-approach" element={<CurriculumApproachPage setPage={setPage} t={t} />} />
        <Route path="/curriculum" element={<CurriculumApproachPage setPage={setPage} t={t} />} />
        <Route path="/accreditation-licences" element={<AccreditationLicensesPage setPage={setPage} t={t} />} />
        <Route path="/accreditations" element={<AccreditationLicensesPage setPage={setPage} t={t} />} />
        <Route path="/accreditation" element={<AccreditationLicensesPage setPage={setPage} t={t} />} />
        <Route path="/leadership-faculty" element={<LeadershipFacultyPage setPage={setPage} t={t} />} />
        <Route path="/school-leadership" element={<LeadershipFacultyPage setPage={setPage} t={t} />} />
        <Route path="/school-facilities" element={<SchoolFacilitiesPage setPage={setPage} t={t} />} />
        <Route path="/campus-facilities" element={<SchoolFacilitiesPage setPage={setPage} t={t} />} />
        <Route path="/facilities" element={<SchoolFacilitiesPage setPage={setPage} t={t} />} />
        <Route path="/preschool" element={<PreschoolPage setPage={setPage} t={t} />} />
        <Route path="/primary-school" element={<PrimarySchoolPage setPage={setPage} t={t} />} />
        <Route path="/middle-school" element={<MiddleSchoolPage setPage={setPage} t={t} />} />
        <Route path="/senior-school" element={<SeniorSchoolPage setPage={setPage} t={t} />} />
        <Route path="/academic-calendar" element={<AcademicCalendarPage t={t} />} />
        <Route path="/school-events" element={<SchoolEventsPage setPage={setPage} t={t} />} />
        <Route path="/well-being-safeguarding" element={<WellbeingSafeguardingPage setPage={setPage} t={t} />} />
        <Route path="/well-being" element={<WellbeingSafeguardingPage setPage={setPage} t={t} />} />
        <Route path="/wellbeing" element={<WellbeingSafeguardingPage setPage={setPage} t={t} />} />
        <Route path="/safeguarding" element={<WellbeingSafeguardingPage setPage={setPage} t={t} />} />
        <Route path="/student-leadership" element={<StudentLeadershipPage setPage={setPage} t={t} />} />
        <Route path="/leadership" element={<StudentLeadershipPage setPage={setPage} t={t} />} />
        <Route path="/play-production" element={<PlayProductionPage setPage={setPage} t={t} />} />
        <Route path="/alumni-community" element={<AlumniCommunityPage setPage={setPage} t={t} />} />
        <Route path="/alumni" element={<AlumniCommunityPage setPage={setPage} t={t} />} />
        <Route path="/community" element={<AlumniCommunityPage setPage={setPage} t={t} />} />
        <Route path="/house-system" element={<HouseSystemPage setPage={setPage} t={t} />} />
        <Route path="/extracurricular-activities" element={<ExtracurricularActivitiesPage setPage={setPage} t={t} />} />
        <Route path="/extracurriculars" element={<ExtracurricularActivitiesPage setPage={setPage} t={t} />} />
        <Route path="/activities" element={<ExtracurricularActivitiesPage setPage={setPage} t={t} />} />
        <Route path="/coming-soon" element={<ComingSoonPage setPage={setPage} />} />
        <Route path="/*" element={
          <>
            <Hero t={t} slideIdx={slideIdx} setSlide={setSlide} setPage={setPage} />
            <Stats t={t} />
            <About t={t} setPage={setPage} />
            <WhyGMIS t={t} whyIdx={whyIdx} setWhyIdx={setWhyIdx} />
            <Accreditations t={t} />
            <Learners t={t} setPage={setPage} />
            <Locations t={t} currentCampus="jakarta" />
            <CTA t={t} setPage={setPage} />
          </>
        } />
      </Routes>

      <Footer t={t} setPage={setPage} />
      <WhatsAppFloatingButton />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
